import { useState, useEffect } from "react";

import { listUploads, listUsers } from "../utils/adminFunctions";
import Spinner from "../Spinner";
import Button from "../Button";

/**
 * Derive the state of an upload from what the uploader GCF recorded.
 *
 * The GCF adds a doc on /register and sets {gcs, uploaded: true} once every
 * chunk has landed, so an upload that was cancelled, crashed or lost its
 * connection simply stays `uploaded: false` forever. Those are the orphans:
 * chunks sitting in the bucket, a Brightcove placeholder waiting on footage
 * that never arrived, and nobody told.
 */
const uploadState = (upload) => {
  // Checked first: a size mismatch means the stored object is not the file the
  // user picked, so it matters more than how far the pipeline got.
  if (upload.integrityOk === false) return "mismatch";
  if (upload.processed) return "processed";
  if (upload.uploaded) return "uploaded";
  return "incomplete";
};

const STATE_STYLE = {
  processed: "bg-green-100 text-green-900",
  uploaded: "bg-blue-100 text-blue-900",
  incomplete: "bg-yellow-100 text-yellow-900",
  mismatch: "bg-red-100 text-red-900",
};

const STATE_LABEL = {
  processed: "Processed",
  uploaded: "Uploaded",
  incomplete: "Never finished",
  mismatch: "Size mismatch",
};

/** `date` is stored as epoch millis by the GCF. */
const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(typeof date === "number" ? date : Number(date));
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
};

/** How old, in days — the input to any future purge policy. */
const ageInDays = (date) => {
  if (!date) return null;
  const ms = Date.now() - Number(date);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

function Uploads() {
  const [uploads, setUploads] = useState();
  // uid -> user, so an upload shows who it came from. The upload doc records
  // only a uid, and "who do I email about this?" is the first question asked
  // when footage turns up unannounced.
  const [usersByUid, setUsersByUid] = useState({});
  const [loadError, setLoadError] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("");

  useEffect(() => {
    listUploads(200)
      .then(setUploads)
      .catch((err) => {
        console.error("Could not load uploads", err);
        setLoadError(err?.message || "Could not load uploads.");
      });
  }, []);

  // Non-blocking: a failure here just means uids stay unresolved.
  useEffect(() => {
    listUsers()
      .then((users) => {
        const byUid = {};
        users.forEach((u) => {
          if (u.uid) byUid[u.uid] = u;
        });
        setUsersByUid(byUid);
      })
      .catch((err) => console.warn("Could not resolve uploader names", err));
  }, []);

  // The raw-footage path records bcid under `user`; newer uploads also carry a
  // `context` with the parent video and sign row.
  const accountOf = (u) => u.context?.accountId || u.user?.bcid || "";

  const accounts = uploads
    ? [...new Set(uploads.map(accountOf).filter(Boolean))].sort()
    : [];

  const visible = (uploads || []).filter((u) => {
    if (stateFilter !== "all" && uploadState(u) !== stateFilter) return false;
    if (accountFilter && accountOf(u) !== accountFilter) return false;
    return true;
  });

  const counts = (uploads || []).reduce((acc, u) => {
    const state = uploadState(u);
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <h1 className="text-2xl font-semibold pb-3">Uploads</h1>
      <p className="text-sm text-gray-600 pb-4">
        Sign language uploads recorded by the uploader function. An upload that
        never finished leaves chunks in the bucket and a Brightcove placeholder
        with no footage — and raises no notification, so this is the only place
        it shows up.
      </p>

      {loadError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {loadError} If this is a permissions error, the <code>uploads</code>{" "}
          collection needs a manager read rule in the Firebase console.
        </p>
      )}

      {!uploads && !loadError && <Spinner />}

      {uploads && (
        <>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex flex-col">
              <label className="text-sm" htmlFor="stateFilter">
                State
              </label>
              <select
                id="stateFilter"
                className="mt-1 px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <option value="all">All ({uploads.length})</option>
                <option value="incomplete">
                  Never finished ({counts.incomplete || 0})
                </option>
                <option value="uploaded">
                  Uploaded ({counts.uploaded || 0})
                </option>
                <option value="processed">
                  Processed ({counts.processed || 0})
                </option>
                <option value="mismatch">
                  Size mismatch ({counts.mismatch || 0})
                </option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm" htmlFor="accountFilter">
                Brightcove account
              </label>
              <select
                id="accountFilter"
                className="mt-1 px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
              >
                <option value="">All accounts</option>
                {accounts.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>

            <Button
              colour="white"
              text="Reload"
              onClick={() => {
                setUploads(undefined);
                setLoadError("");
                listUploads(200)
                  .then(setUploads)
                  .catch((err) => setLoadError(err?.message || "Failed"));
              }}
            />
          </div>

          <div className="mt-4 bg-white rounded border border-gray-200 text-sm">
            <div className="px-3 py-2 grid grid-cols-12 gap-2 text-xs text-gray-600 uppercase font-semibold leading-relaxed">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">File</div>
              <div className="col-span-2">Account</div>
              <div className="col-span-3">Parent video</div>
              <div className="col-span-2">State</div>
            </div>

            {visible.length === 0 && (
              <div className="px-3 py-4 border-t border-gray-200 font-semibold">
                No uploads match this filter.
              </div>
            )}

            {visible.map((upload) => {
              const state = uploadState(upload);
              const age = ageInDays(upload.date);
              return (
                <div
                  key={upload.id}
                  className="px-3 py-3 grid grid-cols-12 gap-2 border-t border-gray-200 items-start"
                >
                  <div className="col-span-2 min-w-0">
                    {formatDate(upload.date)}
                    {age !== null && (
                      <span className="block text-xs text-gray-500">
                        {age} days ago
                      </span>
                    )}
                  </div>

                  <div className="col-span-3 min-w-0 break-words">
                    <span className="font-semibold" title={upload.sourceFile?.name || ""}>
                      {upload.sourceFile?.name || "—"}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {formatSize(upload.sourceFile?.size)}
                    </span>
                  </div>

                  <div className="col-span-2 min-w-0 break-words">
                    {accountOf(upload) || "—"}
                    {(() => {
                      const uid = upload.user?.uid;
                      const who = uid ? usersByUid[uid] : null;
                      if (!uid) {
                        return (
                          <span className="block text-xs text-gray-500">—</span>
                        );
                      }
                      if (!who) {
                        // Unknown uid — a deleted user, or one that never
                        // resolved. The raw id is still the only handle.
                        return (
                          <span className="block text-xs text-gray-500 break-all">
                            {uid}
                          </span>
                        );
                      }
                      const name = [who.first_name, who.last_name]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <>
                          <span className="block text-xs font-semibold text-gray-700">
                            {name || uid}
                          </span>
                          {who.email && (
                            <a
                              className="block text-xs text-gray-500 underline break-all"
                              href={`mailto:${who.email}`}
                            >
                              {who.email}
                            </a>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="col-span-3 min-w-0 break-words">
                    {upload.context?.parentVideoId ? (
                      <>
                        <span>
                          {upload.context.parentName ||
                            upload.context.parentVideoId}
                        </span>
                        <span className="block text-xs text-gray-500">
                          sign {upload.context.signId || "—"} ·{" "}
                          {upload.context.type || "sign_language"}
                        </span>
                      </>
                    ) : (
                      // Uploads predating the `context` field carry nothing
                      // linking them to a placeholder — uid, bcid and a
                      // filename was all the register payload recorded.
                      <span className="text-xs text-gray-500">
                        Not recorded
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 min-w-0">
                    <span
                      className={`${STATE_STYLE[state]} inline-block px-2 py-1 rounded text-xs font-semibold`}
                    >
                      {STATE_LABEL[state]}
                    </span>
                    {state === "mismatch" && (
                      <span className="block mt-1 text-xs text-red-700">
                        stored {upload.composedSize} of {upload.expectedSize}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Showing the 200 most recent. Records live in the production project
            regardless of environment — the uploader endpoint is hardcoded to
            prod.
          </p>
        </>
      )}
    </>
  );
}

export default Uploads;
